import type { UIMessage } from 'ai';
import { createContext, useContext } from 'react';

import {
  useApprovalCardStatuses,
  useDeclineApprovalCard,
} from '../api/use-approval-cards';
import {
  type ApprovalCardRef,
  asToolPart,
  readApprovalCard,
} from '../lib/tool-parts';

/**
 * Whether an approval card from an EARLIER turn can still be answered.
 *
 * A card used to vanish the moment the owner sent anything else ("how much
 * will that cost?"), and on every reload after that, while its approval
 * stayed valid on the server for a day. Claire would then say "approve the
 * card above" over a card that was no longer there, and the owner had nothing
 * to press. Now an earlier card stays on screen while the server says it is
 * unanswered:
 *
 *   - `open`: approving still runs it;
 *   - `expired`: approving asks Claire to raise a fresh card (the server
 *     re-stages an expired approval rather than refusing it);
 *   - `closed`: approved, declined, re-raised by a later card for the same
 *     change, or not known yet. Hidden, as before.
 *
 * Cards of the current turn do not read this: they are always shown.
 */
export type ApprovalCardStatus = 'open' | 'expired' | 'closed';

interface ApprovalCardStatusValue {
  statusOf: (token: string) => ApprovalCardStatus;
  /** False while a turn is running: an answer cannot be sent until it ends. */
  canAnswer: boolean;
  /** Close a card the owner cancelled, so it does not come back. */
  decline: (token: string) => Promise<unknown>;
}

const ApprovalCardStatusContext = createContext<ApprovalCardStatusValue | null>(
  null
);

/** The server checks at most this many cards at once; older ones stay hidden. */
const MAX_TRACKED = 100;

/**
 * Every approval card in the conversation, oldest first, and the tokens of
 * cards a LATER card raised again for the same change (same tool, same
 * item): only the newest of those should ever be pressable.
 */
export function collectApprovalCards(messages: readonly UIMessage[]): {
  cards: ApprovalCardRef[];
  superseded: ReadonlySet<string>;
} {
  const cards: ApprovalCardRef[] = [];
  for (const message of messages) {
    if (message.role !== 'assistant') continue;
    for (const part of message.parts) {
      const tp = asToolPart(part);
      const card = tp ? readApprovalCard(tp) : null;
      if (card) cards.push(card);
    }
  }
  const latest = new Map<string, string>();
  const superseded = new Set<string>();
  for (const card of cards) {
    const change = `${card.executeToolName}\u0000${card.resourceId}`;
    const previous = latest.get(change);
    if (previous && previous !== card.token) superseded.add(previous);
    latest.set(change, card.token);
  }
  return { cards, superseded };
}

export function ApprovalCardStatusProvider({
  messages,
  status,
  children,
}: {
  messages: readonly UIMessage[];
  status: 'submitted' | 'streaming' | 'ready' | 'error';
  children: React.ReactNode;
}) {
  // The AI SDK can mutate a streaming message's parts in place, so scan on
  // every render rather than memoising on the array.
  const { cards, superseded } = collectApprovalCards(messages);
  const tokens = [
    ...new Set(
      cards.map((card) => card.token).filter((t) => !superseded.has(t))
    ),
  ].slice(-MAX_TRACKED);
  // Re-read whenever a turn starts or ends: a card approved by typing "yes"
  // is answered by the turn that ran it.
  const { statuses } = useApprovalCardStatuses(
    tokens,
    `${messages.length}:${status === 'ready' || status === 'error' ? 'idle' : 'busy'}`
  );
  const { declineApprovalCardAsync } = useDeclineApprovalCard();

  const open = new Set(statuses?.open ?? []);
  const expired = new Set(statuses?.expired ?? []);
  const value: ApprovalCardStatusValue = {
    statusOf: (token) =>
      superseded.has(token)
        ? 'closed'
        : open.has(token)
          ? 'open'
          : expired.has(token)
            ? 'expired'
            : 'closed',
    canAnswer: status === 'ready' || status === 'error',
    decline: declineApprovalCardAsync,
  };

  return (
    <ApprovalCardStatusContext.Provider value={value}>
      {children}
    </ApprovalCardStatusContext.Provider>
  );
}

/** Null outside a chat (a card mounted elsewhere keeps its old behaviour). */
export function useApprovalCardStatus(): ApprovalCardStatusValue | null {
  return useContext(ApprovalCardStatusContext);
}
